import {
    useEffect,
    useRef,
} from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
    Map as GoogleMap,
    useMap,
    useMapsLibrary,
} from '@vis.gl/react-google-maps'
import {
    MarkerClusterer,
    Renderer,
} from '@googlemaps/markerclusterer'

import { getMapMarkerIcon } from '../devices/detail/mapMarkers'
import {
    DEFAULT_ZOOM,
    MAP_DEFAULT_CENTER,
    MAP_ID,
    MAX_ZOOM,
} from './constants'

interface DeviceMarker {
    id: string
    lat: number
    lng: number
}

interface Device {
    uid: string
    name?: string
    oilLevelPercentage?: number
    oilAlarm?: boolean
    isMonitored?: boolean
}

interface DevicesState {
    devices: {
        data: Device[]
        markers: DeviceMarker[]
    }
}

const buildMarkerContent = (iconUrl: string): HTMLElement => {
    const wrapper = document.createElement('div')
    wrapper.style.transform = 'translateY(-50%)'

    const img = document.createElement('img')
    img.src = iconUrl
    img.width = 45
    img.height = 45
    img.alt = ''
    img.style.display = 'block'

    wrapper.appendChild(img)

    return wrapper
}

const buildClusterContent = (count: number): HTMLElement => {
    const wrapper = document.createElement('div')
    wrapper.style.cssText = [
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'width:42px',
        'height:42px',
        'border-radius:50%',
        'background:rgba(20,99,156,0.9)',
        'color:#fff',
        'font-weight:700',
        'font-size:14px',
        'box-shadow:0 2px 6px rgba(0,0,0,0.3)',
    ].join(';')
    wrapper.textContent = String(count)

    return wrapper
}

const ClusteredDeviceMarkers = ({
    devices,
    markers,
}: {
    devices: Device[]
    markers: DeviceMarker[]
}) => {
    const map = useMap()
    const markerLib = useMapsLibrary('marker')
    const { t } = useTranslation()
    const navigate = useNavigate()
    const clustererRef = useRef<MarkerClusterer | null>(null)
    const infoWindowRef = useRef<google.maps.InfoWindow | null>(null)

    useEffect(() => {
        if (!map || !markerLib) {
            return
        }

        const infoWindow = new google.maps.InfoWindow()
        infoWindowRef.current = infoWindow

        const advancedMarkers: google.maps.marker.AdvancedMarkerElement[] = []

        markers.forEach(({ id, lat, lng }) => {
            const device = devices.find(item => item.uid === id)

            const advancedMarker = new markerLib.AdvancedMarkerElement({
                position: { lat, lng },
                content: buildMarkerContent(getMapMarkerIcon(
                    device?.oilLevelPercentage,
                    device?.isMonitored,
                    device?.oilAlarm,
                )),
                title: device?.name,
            })

            advancedMarker.addListener('gmp-click', () => {
                const link = `/devices/${id}`
                const oilLevel = device?.oilLevelPercentage ?? 0
                const safeName = device?.name ?? id

                const node = document.createElement('div')
                const linkEl = document.createElement('a')
                linkEl.className = 'mb-1'
                linkEl.href = link
                linkEl.textContent = String(safeName)
                linkEl.addEventListener('click', event => {
                    event.preventDefault()
                    infoWindow.close()
                    navigate(link)
                })

                const para = document.createElement('p')
                para.className = 'mb-1'
                para.textContent = `${t('deviceData.hint.OIL_LEVEL')} - ${oilLevel} %`

                node.appendChild(linkEl)
                node.appendChild(para)

                infoWindow.setContent(node)
                infoWindow.open({ map, anchor: advancedMarker })
            })

            advancedMarkers.push(advancedMarker)
        })

        const renderer: Renderer = {
            render: ({ count, position }) => new markerLib.AdvancedMarkerElement({
                position,
                content: buildClusterContent(count),
                zIndex: 1000 + count,
            }),
        }

        const clusterer = new MarkerClusterer({
            map,
            markers: advancedMarkers,
            renderer,
        })

        clustererRef.current = clusterer

        return () => {
            clusterer.clearMarkers()
            clusterer.setMap(null)
            advancedMarkers.forEach(marker => { marker.map = null })
            infoWindow.close()
            infoWindowRef.current = null
            clustererRef.current = null
        }
    }, [map, markerLib, markers, devices, navigate, t])

    return null
}

const DevicesGoogleMap = () => {
    const devices = useSelector((state: DevicesState) => state.devices.data) ?? []
    const markers = useSelector((state: DevicesState) => state.devices.markers) ?? []

    return (
        <GoogleMap
            mapId={MAP_ID}
            defaultCenter={MAP_DEFAULT_CENTER}
            defaultZoom={DEFAULT_ZOOM}
            maxZoom={MAX_ZOOM}
            className="bg-white devices-overview-map"
            style={{ width: '100%', height: '700px' }}
            gestureHandling="greedy"
            disableDefaultUI={false}
        >
            <ClusteredDeviceMarkers devices={devices} markers={markers} />
        </GoogleMap>
    )
}

export default DevicesGoogleMap
